import { flatMap, flattenDeep, isEmpty, isObject, map, size, uniq, values } from 'es-toolkit/compat';

import { GenerateStudyReportJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/GenerateStudyReport/types';
import { MifMarkerUnsupervisedAnalysisJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMifMarkerUnsupervisedAnalysis/types';
import { MultiplexBlobSegmentationJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexBlobSegmentation/types';
import { MultiplexPeakDetectionJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunMultiplexPeakDetection/types';
import { QaAnalysisJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunQaAnalysis/types';
import { QcAnalysisJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/Multiplex/RunQcAnalysis/types';
import { CellQuantificationJobConfig } from 'components/StudyDashboard/ProceduresPage/Actions/RunQuantification/types';
import { PlotlyFigure } from 'interfaces/agentSession';
import { UsersById } from 'interfaces/userCredentials';
import { RegistrationDetails } from 'utils/slideRegistrations';
import { CasesParams } from 'utils/useCasesParams/types';
import { Inputs } from './calculateFeatures';
import { FlowClassName } from './experimentResults';
import { ExternalSlideTransferJobDetails, ExternalSlideTransferJobResults } from './externalStorage';
import GridBasedCalculationParams from './girdBasedCalculationParams';
import { JobResultColumnDefinition, JobResultSourceConfig } from './jobResultMetadata';
import { CellDetectionDependentJobConfig } from './jobs/basic';
import { CellRules, MultiplexCellTypingConfig, Thresholds } from './jobs/multiplex/cellTypingParams';
import { MultiplexHistogramConfig } from './jobs/multiplex/histogramParams';
import { MultiplexNormalizationJobParams, NormalizationConfig } from './jobs/multiplex/normalizationParams';
import { MultiplexThresholdConfig } from './jobs/multiplex/thresholdParams';
import { PostProcessingActionCreated } from './postProcessingAction';
import { VisualizationCreated } from './visualization';

export interface InferenceParamsNormalizationConfig {
  active: boolean;
  loadParamsFromDb: boolean;
  otfNormalizationConfig: {
    active: boolean;
    normParamsConfig: NormalizationConfig;
  };
}

export interface InferenceParams {
  stainType?: string;
  runType?: string;
  modelPaths?: string[];
  buildNumber?: string;
  heatmapNames?: string[];
  studyId?: string;
  tissueMaskFromAnnotations?: boolean | string;
  assignmentIds?: string[];
  classNames?: string[];
  dedupValue?: string;
  tissueSegmentationModelOverride?: string;
  manifest?: string[] | string;
  version?: string;
  branchName?: string;
  clearmlMachineType: string;
  inferenceVmsLimit?: number;
  skipRunExistingArtifacts?: boolean;
  useDynamicCellDetection?: boolean;
  dynamicCellDetectionConfig?: string;
  normalizationConfig?: InferenceParamsNormalizationConfig;
  channelsToExtract?: string[];
}

// ConcurrentInferenceFlow manifests are arrays of { slide_id } objects, but FlexibleInferenceFlow
// manifests are arrays of plain slide-id strings; both flow classes share this manifest type.
export type InferenceManifestEntry = string | { slide_id: string };
export type InferenceManifest = InferenceManifestEntry[];

export type SlideRegistrationsManifest = { slide_id1: string; slide_id2: string }[];

export interface SecondaryAnalysisInput {
  [caseId: string]: {
    [slideId: string]: {
      inclusionMultipolygon?: any;
      exclusionMultipolygon?: any;
    };
  };
}

export interface StudyMultiplexInputs {
  perMarkerThresholds?: Thresholds;
  perMarkerThresholdsOrchestrationId?: string;
  cellRules?: CellRules;
  knnClassifier?: {
    artifactUrl: string;
    fileExtension: string;
  };
  neighborhoodClustersOrchestrationId?: string;
  kMeansClusteringResults?: ClusteringResult[];
}

export interface CalculateFeaturesParams {
  name?: string; // e.g. generic pips run, cell typing, neighborhood clusters
  manifest: Record<string, string[]>;
  studyId: string;
  postprocessing: PostProcessingActionCreated[];
  features: PostProcessingActionCreated[];
  gridBasedCalculationParams: { [key: string]: GridBasedCalculationParams };
  visualizations: VisualizationCreated[];
  inputs: Inputs;
  pipsStudyInputs?: StudyMultiplexInputs;
  registrations: RegistrationDetails[];
  secondaryAnalysisPolygons?: SecondaryAnalysisInput;
  primaryRunOrchestrationId?: string;
  autoInternallyApprove?: boolean;
}

export interface SecondaryAnalysisSubmissionParams {
  studyId: string;
  manifest: Record<string, string[]>;
  primaryRunOrchestrationId: string;
  secondaryAnalysisPolygons: SecondaryAnalysisInput;
}

export interface ResultForExport {
  experimentResultId: number;
  orchestrationId: string;
  slideId: string;
  originalFileName: string;
  caseId: number;
  caseName: string;
}

export interface QueryStatistics {
  procedures_count: number;
  slides_count: number;
  excluded_procedures_count: number;
  excluded_slides_count: number;
}

export const prepareCustomerResultsResultTypeOptions = [
  'nuclear_segmentations_table',
  'cells_table',
  'areas_table',
  'whole_cell_segmentations_table',
  'polygons_table',
] as const;

export type PrepareCustomerResultsResultType = (typeof prepareCustomerResultsResultTypeOptions)[number];

export interface PrepareCustomerResultsParams {
  resultTypesToExport: PrepareCustomerResultsResultType[];
  zipGranularity?: 'slide' | 'case';
  generateFeaturesCsvFile?: boolean;
  publishResultsToCustomer?: boolean;
  unitsPerZip?: number;
  shardLimit?: number;
  canViewInternalLabels: boolean;
  queryObject?: CasesParams;
  snapshot?: Record<string, { slides: string[] }>;
  unpublishedExperimentResultIds?: number[];
  queryStatistics?: QueryStatistics;
}

export interface SlideRegistrationsParams {
  registratorType: string;
  registrationFeatures: {
    useHematoxylinOrNuclear: boolean;
    useAreaSegmentation: boolean;
    useCellDensities: boolean;
    useTissueMask: boolean;
  };
  cellDetectionArtifactSlide1?: string;
  cellDetectionArtifactSlide2?: string;
  areaSegmentationArtifactSlideId1?: string;
  areaSegmentationArtifactSlideId2?: string;
  machineType?: string;
  evaluationParams?: {
    autoApproveIfBestScore: boolean;
    autoApproveIfFirstScore: boolean;
    sparseRegistratorConfig: string;
  };
  multiplex: boolean;
  multiplexNuclearChannelName?: string;
  docker?: string;
  manifest: SlideRegistrationsManifest;
  studyId: string;
}

export interface CloudOptimizedSlidesSubmissionParams {
  cloudOptimizedConversion?: Record<string, unknown> | null;
  slidesPerJob?: number;
  forceRecreate?: boolean;
}

export interface CloudOptimizedSlidesJobConfig {
  manifest: string[];
  params?: CloudOptimizedSlidesSubmissionParams;
}

export interface TileGenerationExecuteConfig {
  skipTiles?: boolean;
  skipMacro?: boolean;
  skipLabel?: boolean;
  skipChannelHistogram?: boolean;
  skipLevelCheck?: boolean;
  skipCreateTiledChannels?: boolean;
  forceRecoverMultiplexMetadata?: boolean;
}

// Verified production shape: an array of single-entry objects keyed by case id, mapping to that
// case's slide ids (e.g. [{"40937": ["5ea025f5-..."]}]) - not { slides, case_id } as previously assumed.
export type CalculateFeaturesManifestSubTask = Array<Record<string, string[]>>;

export type CalculateFeaturesManifest = Record<string, string[]>;

export interface RunExperimentDatasetReference {
  datasetId: string;
  snapshotTimeUtc?: string;
  datasetUserInputsParams?: Record<string, unknown>;
}

export interface RunExperimentDataLoaderSpecs {
  datasets: RunExperimentDatasetReference[];
  samplerFactory?: unknown;
  dataLoaderFactoryParams?: Record<string, unknown>;
}

export interface RunExperimentClassConfig {
  id?: number;
  dataItems?: string[];
  ignore?: boolean;
  color?: string;
  isPoint?: boolean;
  hollowShape?: boolean;
}

export interface RunExperimentArtifact {
  title?: string;
  fileType?: string;
  storageUrl?: string;
  artifactUrl?: string;
}

export interface RunExperimentCheckpointResults {
  model?: string;
  evaluationData?: RunExperimentArtifact;
  evaluationPlots?: Record<string, Record<string, unknown>>;
}

export interface RunExperimentJobResults {
  tensorboard?: RunExperimentArtifact;
  evaluationData?: RunExperimentArtifact;
  evaluationPlots?: Record<string, Record<string, unknown>>;
  stepResults?: Record<string, RunExperimentCheckpointResults>;
}

export interface RunExperimentJobParams {
  studyId: string;
  trainerDataset: RunExperimentDataLoaderSpecs;
  evaluatorDatasets?: Record<string, RunExperimentDataLoaderSpecs>;
  modelType?: string;
  trainerConfig?: Record<string, unknown>;
  flexibleEvaluatorConfig?: Record<string, unknown>;
  classesConfig?: Record<string, RunExperimentClassConfig>;
  model?: Record<string, unknown>;
  modelProvidedInferenceInputs?: Record<string, unknown>;
  workDir?: Record<string, unknown>;
  experimentName?: string;
  preTrainEvaluation?: boolean;
  resume?: boolean;
  device?: string;
}

export enum JobStatus {
  Pending = 'pending',
  Running = 'running',
  FailedToStart = 'failed_to_start',
  Failed = 'failed',
  Completed = 'completed',
  Cancelled = 'cancelled',
}

export enum JobArchiveState {
  Active = 'active',
  Archived = 'archived',
  All = 'all',
}

export type JobArchiveOperation = 'archive' | 'restore';

export enum JobType {
  PostProcessingAndCalculateFeatures = 'calculate_features',
  RunExperiment = 'run_experiment',
  Inference = 'inference',
  CloudOptimizedSlides = 'cloud_optimized_slides',
  TileGeneration = 'tile_generation',
  MultiplexCellSegmentation = 'multiplex_cell_segmentation',
  PrepareCustomerResults = 'prepare_export_results',
  MultiplexThreshold = 'multiplex_calculate_binary_classifier_thresholds',
  MultiplexNormalization = 'multiplex_normalization',
  MultiplexHistogram = 'multiplex_histogram_creation',
  BinaryClassifier = 'multiplex_binary_classification',
  MultiplexCellTyping = 'multiplex_cell_typing',
  MultiplexPeakDetection = 'multiplex_peak_detection',
  MultiplexBlobSegmentation = 'mplex_blob_segmentation',
  QaAnalysis = 'qa_analysis',
  QcAnalysis = 'qc_analysis',
  GeneralReport = 'general_report',
  StudyReport = 'study_report',
  SlideReport = 'slide_report',
  MifMarkerUnsupervisedAnalysis = 'mif_marker_unsupervised_analysis',
  SlideRegistrations = 'registration',
  MultiplexNeighborhoodClusters = 'multiplex_get_neighborhood_clusters',
  DetectTmaCores = 'detect_tma_cores',
  CropTma = 'crop_tma',
  NucleaiExecutor = 'nucleai_executor',
  NeighborhoodClusters = 'neighborhood_clusters',
  CellSegmentation = 'brightfield_cell_segmentation',
  CellClassification = 'classify_cells',
  MultiplexTissueSegmentationMask = 'multiplex_tissue_segmentation_mask',
  ManualPostIngestPipeline = 'manual_post_ingest_pipeline',
  BrightfieldTissueSegmentationMask = 'brightfield_tissue_segmentation_mask',
  CellQuantification = 'cell_quantification',
  VentanaMock = 'ventana_mock',
  Ingest = 'ingest',
  BatchIngest = 'batch_ingest',
  ExternalSlideTransfer = 'external_slide_transfer',
}

/** One `timeline.log` entry written by the ingest and tile generation workers; timestamps are UTC without an offset. */
export interface JobTimelineLogEntry {
  event: string;
  type?: string;
  timestamp?: string;
}

/** `timeline.progress`: the current step and overall percent, written by the tile generation worker. */
export interface JobTimelineProgress {
  step: string;
  stepNumber: number;
  stepCount: number;
  percent: number;
  message: string;
}

/** Writers own the timeline schema; algo jobs store an event array instead of this object. */
export type JobTimeline = { log?: JobTimelineLogEntry[]; progress?: JobTimelineProgress | null } | unknown[];

type CommonJobFields = {
  updatedAt?: string;
  timeline?: JobTimeline | null;
  deletedAt?: string | null;
  deletedBy?: string | null;
  status?: string;
  userId?: string;
  submittedBy?: string;
  usersById?: UsersById;
  childStatusCounts?: Record<string, number>;
  type?: JobType;
  name?: string;
  externalTaskId?: string;
  description?: string;
  externalTaskLink?: string;
  requestSender?: string;
  statusMessage?: string;
  studyId?: string;
  orchestrationId?: string;
  startedAt?: string;
  id?: string;
  children?: Job[];
  parentId?: string;
  isParentJob?: boolean;
};

export type ExternalSlideTransferJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.ExternalSlideTransfer;
  params: ExternalSlideTransferJobDetails;
  manifest?: undefined | null;
  results?: ExternalSlideTransferJobResults;
};

export type CalculateFeaturesJob = CommonJobFields & {
  flowClassName: FlowClassName.CalculateFeatures;
  type: JobType.PostProcessingAndCalculateFeatures;
  params: CalculateFeaturesParams;
  manifest: CalculateFeaturesManifest | CalculateFeaturesManifestSubTask;
  primaryRunOrchestrationId?: string; // legacy mirror of params.primaryRunOrchestrationId
};

export type InferenceJob = CommonJobFields & {
  flowClassName: FlowClassName.ConcurrentInferenceFlow | FlowClassName.FlexibleInferenceFlow;
  type: JobType.Inference;
  params: InferenceParams;
  manifest: InferenceManifest;
};

export type CloudOptimizedSlidesJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.CloudOptimizedSlides;
  params: CloudOptimizedSlidesSubmissionParams | CloudOptimizedSlidesJobConfig;
  manifest: string[];
};

export type TileGenerationJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.TileGeneration;
  params: {
    procedureParams?: CasesParams;
    configParams?: TileGenerationExecuteConfig;
    [key: string]: unknown;
  };
  manifest: string[];
};

export type RunExperimentJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.RunExperiment;
  params: RunExperimentJobParams;
  manifest?: string[];
  results?: RunExperimentJobResults;
};

export interface CommonJobResult {
  title?: string;
  index?: number;
  is_group?: boolean;
  links?: {
    cases: Array<{ id: number; display_name: string }>;
    slides: Array<{ id: string; display_name: string }>;
  };
}

export interface ReportJobResult extends CommonJobResult, PlotlyFigure {
  data: any;
  layout: any;
  file_type: 'json';
  description?: string;
  storage_url?: string;
  url?: string;
  sourceConfig?: JobResultSourceConfig;
  is_group?: false;
  results?: undefined;
}

export interface ReportJobParquetResult extends CommonJobResult {
  file_type: 'parquet';
  description?: string;
  storage_url?: string;
  url?: string;
  artifact_url?: string;
  sourceConfig?: JobResultSourceConfig;
  columns?: JobResultColumnDefinition[];
  is_group?: false;
  results?: undefined;
}

export type RenderableReportJobResult = ReportJobResult | ReportJobParquetResult;

export interface NestedReportJobResult extends CommonJobResult {
  is_group: true;
  results?: Record<string, RenderableReportJobResult>;
}

export type ReportJobResultGroup = Record<string, RenderableReportJobResult | NestedReportJobResult>;

export const isNestedReportJobResult = (
  result: RenderableReportJobResult | NestedReportJobResult
): result is NestedReportJobResult => result?.is_group === true;

export const isReportJobChartResult = (result: unknown): result is ReportJobResult => {
  const nestedResult = result as NestedReportJobResult;
  const chartResult = result as ReportJobResult;

  return (
    isObject(result) &&
    nestedResult?.is_group !== true &&
    (chartResult?.file_type ?? 'json') === 'json' &&
    ('data' in result || 'layout' in result || 'storage_url' in result || 'url' in result)
  );
};

export const isReportJobParquetResult = (result: unknown): result is ReportJobParquetResult => {
  const nestedResult = result as NestedReportJobResult;
  const parquetResult = result as ReportJobParquetResult;

  return (
    isObject(result) &&
    nestedResult?.is_group !== true &&
    parquetResult?.file_type === 'parquet' &&
    ('storage_url' in result || 'url' in result)
  );
};

export type ReportJobResults = Record<string, ReportJobResultGroup>;

type WrappedReportJobArtifactResult = CommonJobResult & {
  description?: string;
  artifact?: unknown;
  sourceConfig?: JobResultSourceConfig;
  columns?: JobResultColumnDefinition[];
};

const unwrapWrappedReportJobArtifactResult = (result: unknown): RenderableReportJobResult | undefined => {
  if (!isObject(result)) {
    return undefined;
  }

  const artifact = (result as WrappedReportJobArtifactResult).artifact;
  if (!isReportJobChartResult(artifact) && !isReportJobParquetResult(artifact)) {
    return undefined;
  }

  const wrappedResult = result as WrappedReportJobArtifactResult;

  if (isReportJobParquetResult(artifact)) {
    return {
      ...artifact,
      title: wrappedResult.title ?? artifact.title,
      description: wrappedResult.description ?? artifact.description,
      index: wrappedResult.index ?? artifact.index,
      links: wrappedResult.links ?? artifact.links,
      sourceConfig: wrappedResult.sourceConfig ?? artifact.sourceConfig,
      columns: wrappedResult.columns ?? artifact.columns,
    };
  }

  return {
    ...artifact,
    title: wrappedResult.title ?? artifact.title,
    description: wrappedResult.description ?? artifact.description,
    index: wrappedResult.index ?? artifact.index,
    links: wrappedResult.links ?? artifact.links,
    sourceConfig: wrappedResult.sourceConfig ?? artifact.sourceConfig,
  };
};

const normalizeReportJobResultEntry = (
  result: unknown
): RenderableReportJobResult | NestedReportJobResult | undefined => {
  if (isObject(result) && (result as NestedReportJobResult).is_group === true) {
    const nestedResult = result as NestedReportJobResult;
    const normalizedNestedResults = Object.fromEntries(
      Object.entries(nestedResult.results || {}).flatMap(([resultKey, childResult]) => {
        const normalizedResult = normalizeReportJobResultEntry(childResult);
        return normalizedResult && !isNestedReportJobResult(normalizedResult) ? [[resultKey, normalizedResult]] : [];
      })
    ) as Record<string, RenderableReportJobResult>;

    return isEmpty(normalizedNestedResults) ? undefined : { ...nestedResult, results: normalizedNestedResults };
  }

  if (isReportJobChartResult(result) || isReportJobParquetResult(result)) {
    return result;
  }

  return unwrapWrappedReportJobArtifactResult(result);
};

const normalizeReportJobResultGroup = (resultGroup: unknown): ReportJobResultGroup | undefined => {
  if (!isObject(resultGroup)) {
    return undefined;
  }

  const normalizedEntries = Object.fromEntries(
    Object.entries(resultGroup).flatMap(([resultKey, result]) => {
      const normalizedResult = normalizeReportJobResultEntry(result);
      return normalizedResult ? [[resultKey, normalizedResult]] : [];
    })
  ) as ReportJobResultGroup;

  return isEmpty(normalizedEntries) ? undefined : normalizedEntries;
};

export const isReportJobResultEntry = (result: unknown): result is RenderableReportJobResult | NestedReportJobResult =>
  normalizeReportJobResultEntry(result) !== undefined;

export const isReportJobResultGroup = (resultGroup: unknown): resultGroup is ReportJobResultGroup =>
  Boolean(normalizeReportJobResultGroup(resultGroup));

export const getRenderableReportJobResults = (results: unknown): ReportJobResults => {
  if (!isObject(results)) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(results).flatMap(([resultGroupKey, resultGroup]) => {
      const normalizedResultGroup = normalizeReportJobResultGroup(resultGroup);
      return normalizedResultGroup ? [[resultGroupKey, normalizedResultGroup]] : [];
    })
  ) as ReportJobResults;
};

export const isReportJobResults = (results: unknown): results is ReportJobResults =>
  !isEmpty(getRenderableReportJobResults(results));

export type QaAnalysisJob = CommonJobFields & {
  type: JobType.QaAnalysis;
  manifest: string[];
  params: QaAnalysisJobConfig;
  flowClassName?: undefined | null;
  results: ReportJobResults;
};

export type QcAnalysisJob = CommonJobFields & {
  type: JobType.QcAnalysis;
  manifest: string[];
  params: QcAnalysisJobConfig;
  flowClassName?: undefined | null;
  results: ReportJobResults;
};

export type MifMarkerUnsupervisedAnalysisJob = CommonJobFields & {
  type: JobType.MifMarkerUnsupervisedAnalysis;
  manifest: string[];
  params: MifMarkerUnsupervisedAnalysisJobConfig;
  flowClassName?: undefined | null;
  results: ReportJobResults;
};

export type StudyReportJob = CommonJobFields & {
  type: JobType.StudyReport;
  manifest: string[];
  params: GenerateStudyReportJobConfig;
  flowClassName?: undefined | null;
  results: ReportJobResults;
};

export interface GeneralReportJobParams {
  html_report_artifact_url?: string;
  html_report_presigned_url?: string;
  [key: string]: unknown;
}

export type GeneralReportJob = CommonJobFields & {
  type: JobType.GeneralReport;
  manifest: string[];
  params: GeneralReportJobParams;
  flowClassName: FlowClassName.GeneralReportFlow;
  results?: ReportJobResults;
};

export type SlideReportJob = CommonJobFields & {
  type: JobType.StudyReport;
  manifest: string[];
  params: any;
  flowClassName?: undefined | null;
  results: ReportJobResults;
};

export type ReportJobTypes =
  | GeneralReportJob
  | StudyReportJob
  | SlideReportJob
  | QaAnalysisJob
  | QcAnalysisJob
  | MifMarkerUnsupervisedAnalysisJob;

export const isLegacyPrepareCustomerResultsManifest = (manifest: ResultForExport[] | string[]): manifest is string[] =>
  size(manifest) > 0 && typeof manifest[0] === 'string';

export type PrepareCustomerResultsJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.PrepareCustomerResults;
  params: PrepareCustomerResultsParams;
  manifest: ResultForExport[] | string[]; // string[] is legacy, a list of slide ids
};

type PrepareCustomerResultsJobLike = Partial<Pick<CommonJobFields, 'type' | 'parentId' | 'isParentJob'>>;

export const isPrepareCustomerResultsChildJob = (job?: PrepareCustomerResultsJobLike) => {
  return Boolean(job?.type === JobType.PrepareCustomerResults && job?.parentId);
};

export const isPrepareCustomerResultsParentJob = (job?: PrepareCustomerResultsJobLike) => {
  if (job?.type !== JobType.PrepareCustomerResults) {
    return false;
  }

  return job?.isParentJob === true || !job?.parentId;
};

export type IngestJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.Ingest;
  params: {
    lab_id?: string;
    source_slide_uri?: string;
    [key: string]: any;
  };
  manifest: string[];
  results?: {
    slide_id?: string;
    ingested_slide_uri?: string;
    [key: string]: any;
  };
};

export interface IngestJobAttempt {
  jobId: string;
  isCurrentJob: boolean;
  status?: string;
  startedAt?: string;
  updatedAt?: string;
  statusMessage?: string;
  slideId?: string;
  ingestedSlideUri?: string;
  sourceSlideUri?: string;
}

export interface IngestSlide {
  slideId: string;
  labId: string;
  originalFileName?: string;
  slideFormat?: string;
  createdAt?: string;
  scanDate?: string;
}

export interface IngestJobDetails {
  jobId: string;
  labId?: string;
  currentJobIsLatestAttempt: boolean;
  overallStatus: string;
  latestIngestStatus?: string;
  jobAttemptCount: number;
  slideRecordCount: number;
  slideIds: string[];
  slideFormats: string[];
  reprocessingDetected: boolean;
  warnings: string[];
  jobAttempts: IngestJobAttempt[];
  slides: IngestSlide[];
}

export type BatchIngestJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.BatchIngest;
  params: {
    lab_id?: string;
    study_id?: string;
    folder_path?: string;
    upload_keys?: string[];
    [key: string]: any;
  };
  manifest: string[];
};

export type NormalizationJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.MultiplexNormalization;
  params: { histogramNormalization: MultiplexNormalizationJobParams; histogramCreation: MultiplexHistogramConfig };
  manifest: string[];
};

export type HistogramJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.MultiplexHistogram;
  params: MultiplexHistogramConfig;
  manifest: string[];
};

export type ThresholdJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.MultiplexThreshold;
  params: MultiplexThresholdConfig;
  manifest: string[];
  results: {
    perMarkerThresholds: { [key: string]: number };
  };
};

export const hashDepUrlParameterRegex = /hash_deps=[a-f0-9]+/;

export type CellTypingJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.MultiplexCellTyping;
  params: MultiplexCellTypingConfig;
  manifest: string[];
  results: {
    slideInferenceMultiplexMarkersProbabilities: {
      artifactUrl: string;
      fileType: string;
    };
  };
};

export type BlobSegmentationJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.MultiplexBlobSegmentation;
  params: MultiplexBlobSegmentationJobConfig;
  manifest: string[];
};

export type PeakDetectionJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.MultiplexPeakDetection;
  params: MultiplexPeakDetectionJobConfig;
  manifest: string[];
};

export type CellDetectionDependentJob = CommonJobFields & {
  params: CellDetectionDependentJobConfig;
};

export type CellClassificationJob = CellDetectionDependentJob & {
  flowClassName: string;
  type: JobType.CellClassification;
  manifest: string[];
};

export type CellSegmentationJob = CellDetectionDependentJob & {
  flowClassName: string;
  type: JobType.CellSegmentation;
  manifest: string[];
};

export type CellQuantificationJob = CommonJobFields & {
  flowClassName?: string;
  type: JobType.CellQuantification;
  manifest: string[];
  params: CellQuantificationJobConfig;
};

export type NucleaiExecutorJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.NucleaiExecutor;
  params: any;
  manifest: Array<{
    case_id: number;
    slides: Record<string, string>; // stain type to slide id
  }>;
  results: any;
};

export interface KMeansParams {
  k?: number;
  minK?: number;
  maxK?: number;
  numIterations: number;
  numRedo: number;
}

export interface NeighborCountParams {
  nNeighbors: number;
  normalizeCounts?: boolean;
  arcsinhNormalizationCoFactor?: number;
  countColumnPrefix?: string;
}

export interface ClusteringResult {
  kMeansParams: KMeansParams;
  neighborCountParams: NeighborCountParams;
  clusteringCellLabelsOrder?: string[];
  clusteringCentroids: number[][];
  davidBouldinScore: number;
  wssScore: number;
  cellFractionsPerCluster: number[][];
  // Default (to be set in implementation): internallyApproved = false
  internallyApproved?: boolean;
}

export interface NeighborClustersJobResults {
  kMeansClusteringResults: ClusteringResult[];
}

export type NeighborhoodClustersJob = CommonJobFields & {
  flowClassName?: undefined | null;
  type: JobType.MultiplexNeighborhoodClusters | JobType.NeighborhoodClusters;
  manifest: string[];
  results: NeighborClustersJobResults;
  params: { [key: string]: any }; // this should be more specific when adding the execution of the job to the app
};

export type TmaCoreBoundingBox = [number, number, number, number];
export type TmaCoreResults = Record<string, TmaCoreBoundingBox>;
export type DetectTmaCoresResults = TmaCoreResults | Record<string, TmaCoreResults>;
export interface TmaCoreOverrideSnapshot {
  cores: TmaCoreResults;
  updatedBy: string;
  updatedAt: string;
}
export type DetectTmaCoresJobResults = Record<string, unknown>;

export type DetectTmaCoresJob = CommonJobFields & {
  id: string;
  flowClassName?: undefined | null;
  type: JobType.DetectTmaCores;
  manifest: string[];
  params: { manifest?: string[]; studyId?: string; study_id?: string; [key: string]: unknown };
  results?: DetectTmaCoresJobResults;
};

export type RegistrationJob = CommonJobFields & {
  flowClassName: FlowClassName.PerformRegistrationFlow;
  type: JobType.SlideRegistrations;
  params: SlideRegistrationsParams;
  manifest: SlideRegistrationsManifest;
};

export type Job =
  | CalculateFeaturesJob
  | RunExperimentJob
  | InferenceJob
  | IngestJob
  | BatchIngestJob
  | ExternalSlideTransferJob
  | CloudOptimizedSlidesJob
  | TileGenerationJob
  | PrepareCustomerResultsJob
  | NormalizationJob
  | HistogramJob
  | ThresholdJob
  | RegistrationJob
  | CellTypingJob
  | PeakDetectionJob
  | BlobSegmentationJob
  | NeighborhoodClustersJob
  | DetectTmaCoresJob
  | NucleaiExecutorJob
  | CellClassificationJob
  | CellSegmentationJob
  | CellQuantificationJob
  | ReportJobTypes;

type SecondaryAnalysisJobLike = Pick<CalculateFeaturesJob, 'primaryRunOrchestrationId' | 'params'> &
  Partial<Pick<Job, 'type'>>;

export const getSecondaryAnalysisJobPrimaryRunOrchestrationId = (
  job?: Partial<Job | SecondaryAnalysisJobLike>
): string | undefined => {
  if (job?.type && job?.type !== JobType.PostProcessingAndCalculateFeatures) {
    // Only PostProcessingAndCalculateFeatures jobs are considered secondary analysis jobs,
    // so if the type is defined and not that, we return undefined
    return undefined;
  }

  const secondaryAnalysisJob = job as Partial<SecondaryAnalysisJobLike>;
  return secondaryAnalysisJob?.primaryRunOrchestrationId || secondaryAnalysisJob?.params?.primaryRunOrchestrationId;
};

export const isSecondaryAnalysisJob = (job?: SecondaryAnalysisJobLike) => {
  const hasPrimaryRunOrchestrationId = Boolean(getSecondaryAnalysisJobPrimaryRunOrchestrationId(job));
  if (!hasPrimaryRunOrchestrationId) {
    return false;
  }

  return !job?.type || job.type === JobType.PostProcessingAndCalculateFeatures;
};

const rerunnableJobTypes: JobType[] = [
  JobType.Inference,
  JobType.MultiplexNormalization,
  JobType.PostProcessingAndCalculateFeatures,
  JobType.TileGeneration,
];

export const isRerunnableJob = (job?: Partial<Pick<Job, 'type'>>) => {
  return Boolean(job?.type && rerunnableJobTypes.includes(job.type));
};

export interface JobResponse<JobType extends Job = Job> {
  jobs: JobType[];
  totalJobs: number;
  usersById?: UsersById;
}

export interface JobArchiveResponse<JobType extends Job = Job> {
  operation: JobArchiveOperation;
  job: JobType;
  directResultTargetCount: number;
  directResultChangedCount: number;
  affectedStudyIds: string[];
}

export interface RejectedIngestCaseAssignmentRemovalResponse {
  jobId: string;
  slideId: string;
  affectedStudyIds: string[];
}

export interface IngestArchiveImpactResponse {
  slideId: string | null;
  hasOtherUnarchivedIngest: boolean;
}

export interface JobNotification {
  arn?: string;
  id?: string;
  payload: Record<string, any>;
}

export const getJobManifestSlideIds = (job: Job): string[] => {
  if (job.type === JobType.PostProcessingAndCalculateFeatures) {
    return flattenDeep(values(job.manifest as CalculateFeaturesManifest));
  }

  if (job.type === JobType.PrepareCustomerResults && !isLegacyPrepareCustomerResultsManifest(job.manifest)) {
    return map(job.manifest, 'slideId');
  }

  if (job.type === JobType.SlideRegistrations) {
    return uniq(flatMap(job.manifest, ({ slide_id1, slide_id2 }) => [slide_id1, slide_id2]));
  }

  return job.manifest as string[];
};
